import { useEffect, useRef, useState, type ReactNode } from "react";

const reduced = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * 3D stage for the hero: perspective + mouse tilt + depth layers (data-depth on children) +
 * gentle scroll parallax. Pure transforms, no WebGL, no layout work per frame.
 */
export function Stage3D({ children, className = "" }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current; if (!el || reduced()) return;
    let rx = 0, ry = 0, tx = 0, ty = 0, raf = 0, scrollY = 0;
    const layers = Array.from(el.querySelectorAll<HTMLElement>("[data-depth]"));
    const render = () => {
      raf = 0;
      rx += (tx - rx) * 0.12; ry += (ty - ry) * 0.12;
      el.style.transform = `rotateX(${rx}deg) rotateY(${ry}deg) translateY(${scrollY * -0.06}px)`;
      for (const l of layers) {
        const d = Number(l.dataset.depth || 0);
        l.style.transform = `translate3d(${ry * d * 0.9}px, ${-rx * d * 0.9}px, ${d * 12}px)`;
      }
      if (Math.abs(tx - rx) > 0.05 || Math.abs(ty - ry) > 0.05) raf = requestAnimationFrame(render);
    };
    const kick = () => { if (!raf) raf = requestAnimationFrame(render); };
    const onMove = (e: MouseEvent) => {
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5, py = (e.clientY - r.top) / r.height - 0.5;
      tx = -py * 10; ty = px * 12; kick();
    };
    const onLeave = () => { tx = 0; ty = 0; kick(); };
    const onScroll = () => { scrollY = Math.min(window.scrollY, 600); kick(); };
    const parent = el.parentElement || el;
    parent.addEventListener("mousemove", onMove); parent.addEventListener("mouseleave", onLeave);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { parent.removeEventListener("mousemove", onMove); parent.removeEventListener("mouseleave", onLeave); window.removeEventListener("scroll", onScroll); if (raf) cancelAnimationFrame(raf); };
  }, []);
  return (
    <div className={`fs-stage-wrap ${className}`}>
      <div ref={ref} className="fs-stage">{children}</div>
    </div>
  );
}

/** Headline that reveals word by word when it scrolls into view. */
export function Words({ text, className = "" }: { text: string; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setOn(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setOn(true); io.disconnect(); } }, { threshold: 0.4 });
    io.observe(el); return () => io.disconnect();
  }, []);
  return (
    <span ref={ref} className={`fs-words ${on ? "is-on" : ""} ${className}`}>
      {text.split(" ").map((w, i) => (
        <span key={i} className="fs-word" style={{ transitionDelay: `${i * 55}ms` }}>{w}&nbsp;</span>
      ))}
    </span>
  );
}

/** 0..1 progress of a tall section while it is pinned (sticky child). */
export function useScrollProgress(ref: React.RefObject<HTMLElement>) {
  const [p, setP] = useState(0);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    let raf = 0;
    const calc = () => {
      raf = 0;
      const r = el.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      const v = total <= 0 ? 0 : Math.min(1, Math.max(0, -r.top / total));
      setP((prev) => (Math.abs(prev - v) > 0.002 ? v : prev));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(calc); };
    calc(); window.addEventListener("scroll", onScroll, { passive: true }); window.addEventListener("resize", onScroll);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [ref]);
  return p;
}

/** Infinite horizontal strip; pauses on hover. */
export function Marquee({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`fs-marquee ${className}`}>
      <div className="fs-marquee-track">{children}{children}</div>
    </div>
  );
}
